import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, LockClosedIcon, LockOpenIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { useMessageDetail, useReplyMessage, useToggleMessageStatus } from "http-hooks/messages/useMessages";

const MessageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [replyText, setReplyText] = useState("");

  const { data, isLoading, refetch } = useMessageDetail(id);
  const { mutateAsync: reply, isPending: isReplying } = useReplyMessage();
  const { mutateAsync: toggle, isPending: isToggling } = useToggleMessageStatus();

  const thread = data?.thread;
  const messages = data?.messages || [];
  const otherParty = data?.otherParty;

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    try {
      await reply({ id, data: { comment_text: replyText.trim() } });
      setReplyText("");
      refetch();
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggle = async () => {
    try {
      await toggle(id);
      refetch();
    } catch (e) {
      console.error(e);
    }
  };

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <p className="text-sm text-gray-500 mt-8">Loading...</p>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 mt-8">
        <p className="text-sm text-red-500">Message not found.</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/messages")}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{thread.comment_subject}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              With: <span className="font-medium text-gray-700">{otherParty?.name}</span>
              {" · "}
              <span className={thread.comment_status === 1 ? "text-green-600" : "text-red-500"}>
                {thread.comment_status === 1 ? "Open" : "Closed"}
              </span>
            </p>
          </div>
        </div>
        <button
          onClick={handleToggle}
          disabled={isToggling}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold shadow-sm disabled:opacity-50 ${
            thread.comment_status === 1
              ? "bg-red-50 text-red-700 hover:bg-red-100"
              : "bg-green-50 text-green-700 hover:bg-green-100"
          }`}
        >
          {thread.comment_status === 1 ? (
            <><LockClosedIcon className="h-4 w-4" /> Close</>
          ) : (
            <><LockOpenIcon className="h-4 w-4" /> Reopen</>
          )}
        </button>
      </div>

      {/* Message Thread */}
      <div className="space-y-3 mb-6">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.myself ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[75%] rounded-xl px-4 py-3 shadow-sm ${
                msg.myself
                  ? "bg-indigo-600 text-white rounded-br-none"
                  : "bg-white ring-1 ring-gray-200 text-gray-900 rounded-bl-none"
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.comment_text}</p>
              <p className={`text-xs mt-1 ${msg.myself ? "text-indigo-200" : "text-gray-400"}`}>
                {formatDate(msg.comment_timestamp)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Reply Box */}
      {thread.comment_status === 1 ? (
        <div className="bg-white ring-1 ring-gray-200 rounded-xl p-4">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply..."
            rows={3}
            maxLength={500}
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <div className="mt-2 flex justify-between items-center">
            <span className="text-xs text-gray-400">{replyText.length}/500</span>
            <button
              onClick={handleReply}
              disabled={!replyText.trim() || isReplying}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
              {isReplying ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-500 text-center">
          This thread is closed. Reopen it to reply.
        </div>
      )}
    </div>
  );
};

export default MessageDetail;
